import React, {useCallback, useEffect} from 'react'
import {TaskEditorWindow} from './TaskEditorWindow'
import {useDispatch, useSelector} from 'react-redux'
import {QeSqlQueryDto, QuerySplitter, SocketEventTypeEnum, TaskDto, TaskType, WsQueryDto} from 'learndb-share'
import {IWindow, WindowEnum} from '../../domain/IWindow'
import {IReduxState} from '../../../../redux/types/IReduxState'
import {taskActions} from '../../redux/actions'
import {removeStorageData} from '../../../../../storage/methods'
import {LS_EDITOR_CURRENT_QUERY} from '../../constant'
import {getAuthorizationHeader} from '../../../auth/utils'
import {apiPost} from '../../../../api'
import {ComponentEnum} from '../../componentsRegistry/Registry'

interface ITaskEditorWrapperProps {
  topLeftWindow: IWindow
  bottomLeftWindow: IWindow
  topRightWindow: IWindow
  bottomRightWindow: IWindow
}

declare const socket: any

const TaskEditorWrapper: React.FC<ITaskEditorWrapperProps> = ({
  topLeftWindow,
  bottomLeftWindow,
  topRightWindow,
  bottomRightWindow,
}): React.ReactElement => {
  const taskDispatch = useDispatch()
  const task = useSelector<IReduxState, TaskDto | undefined>((state) => state.task?.task)
  const taskId = task?.taskId
  const taskTypeId = task?.taskTypeId
  const query = useSelector<IReduxState, string | undefined>((state) => state.task?.query)

  const isExecuting = useSelector<IReduxState, boolean>((state) => state.task?.isExecuting)

  const getUserQueries = useCallback((): QeSqlQueryDto[] => {
    if (taskTypeId === TaskType.SELECT) {
      return [
        {
          id: '1',
          lineStart: 1,
          text: query,
        },
      ]
    } else {
      const splitter = new QuerySplitter()
      return splitter.split(query)
    }
  }, [query, taskTypeId])

  const handleExecutionResponse = (response): void => {
    const responseData = JSON.parse(response)
    taskDispatch(
      taskActions.setState({
        isExecuting: false,
        queryResultUser: responseData?.queryResultUser,
        queryResultSolution: responseData?.queryResultSolution,
        checkResults: responseData?.checkResults,
        sqlResults: responseData?.sqlResults,
        isQueryResultEquals: responseData?.isQueryResultEquals,
      }),
    )
    if (responseData.isQueryResultEquals) {
      removeStorageData(LS_EDITOR_CURRENT_QUERY)
      taskDispatch(taskActions.getTask(taskId))
    }
  }

  useEffect(() => {
    if (socket) {
      socket.on(SocketEventTypeEnum.EXECUTE_TASK, handleExecutionResponse)

      return () => {
        socket.off(SocketEventTypeEnum.EXECUTE_TASK, handleExecutionResponse)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleExecute = useCallback(async () => {
    if (!isExecuting) {
      taskDispatch(taskActions.setState({isExecuting: true}))
      const message: WsQueryDto = {
        type: 'QUERY',
        ...getAuthorizationHeader(),
        taskId,
        queriesUser: getUserQueries(),
      } as WsQueryDto
      await apiPost('/query/execute', message)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUserQueries])

  const executeEventListener = useCallback(
    (evt): void => {
      if ((evt.key === 'Enter' && evt.metaKey) || (evt.key === 'Enter' && evt.ctrlKey)) {
        handleExecute()
      }
    },
    [handleExecute],
  )

  useEffect(() => {
    document.addEventListener('keydown', executeEventListener)
    return () => {
      document.removeEventListener('keydown', executeEventListener)
    }
  }, [executeEventListener])

  const splitTabs = (
    leftComponentId: ComponentEnum,
    rightComponentId: ComponentEnum,
    fullscreenActiveLeftComponentId: ComponentEnum,
    fullscreenActiveRightComponentId: ComponentEnum,
  ): void => {
    taskDispatch(
      taskActions.changeFullscreenMode(
        true,
        true,
        leftComponentId,
        rightComponentId,
        fullscreenActiveLeftComponentId,
        fullscreenActiveRightComponentId,
      ),
    )
  }

  return (
    <>
      <div className={'task-editor-half-width-column'}>
        <div key={'task-editor-left'}>
          <TaskEditorWindow
            key={WindowEnum.topLeftWindow}
            type={WindowEnum.topLeftWindow}
            windowProp={topLeftWindow}
            splitTabs={splitTabs}
          />
          <TaskEditorWindow
            splitTabs={splitTabs}
            key={WindowEnum.bottomLeftWindow}
            type={WindowEnum.bottomLeftWindow}
            windowProp={bottomLeftWindow}
          />
        </div>
      </div>
      <div className={'task-editor-half-width-column'}>
        <div key={'task-editor-right'}>
          <TaskEditorWindow
            key={WindowEnum.topRightWindow}
            type={WindowEnum.topRightWindow}
            windowProp={topRightWindow}
            isExecuting={isExecuting}
            handleExecute={handleExecute}
            splitTabs={splitTabs}
          />
          <TaskEditorWindow
            key={WindowEnum.bottomRightWindow}
            type={WindowEnum.bottomRightWindow}
            windowProp={bottomRightWindow}
            splitTabs={splitTabs}
          />
        </div>
      </div>
    </>
  )
}

export {TaskEditorWrapper}
